import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, User, Mail, Phone, Shield, Building, Save, Briefcase, Loader2 } from 'lucide-react';
import { resetMutationState, updateUser } from '../../../features/userManagement/userManagementSlice';
import { fetchClients } from '../../../features/onboarding/onboardingSlice';
import {
  fetchAvailableRoles,
  selectAvailableRoles,
  selectIsLoadingAvailableRoles,
} from '../../../features/rbac/rbacSlice';

export default function EditUserModal({ user, onClose }) {
  const dispatch = useDispatch();
  const {
    updateStatus,
    updateError,
    updatingUserId
  } = useSelector((state) => state.userManagement);
  const { clients: apiClients, clientsStatus } = useSelector((state) => state.onboarding);

  // Get roles from RBAC slice (fetched from /api/auth/roles)
  const availableRoles = useSelector(selectAvailableRoles);
  const isLoadingRoles = useSelector(selectIsLoadingAvailableRoles);

  // Map API clients to the expected format { id, name }
  const clients = useMemo(() => {
    return (apiClients || []).map(client => ({
      id: client.clientUUID || client.id,
      name: client.clientName || client.name
    }));
  }, [apiClients]);

  // Helper to parse client - handle both string and object formats
  const parseClient = (clientData) => {
    if (!clientData) return null;
    if (typeof clientData === 'object' && clientData.id) {
      return clientData;
    }
    if (typeof clientData === 'string' && clientData.trim()) {
      // Try to find matching client by name for backward compatibility
      const foundClient = clients.find(c => c.name === clientData);
      return foundClient || null;
    }
    return null;
  };

  // Helper to get roleId from user data (handle both roleId and role name)
  const getUserRoleId = (userData) => {
    // If user already has roleId (UUID), use it
    if (userData?.roleId) {
      return userData.roleId;
    }
    // If user only has role name, find matching role
    if (userData?.role && availableRoles.length > 0) {
      const matchingRole = availableRoles.find(r => r.name === userData.role);
      return matchingRole?.id || '';
    }
    return '';
  };

  const departments = [
    'Security Operations',
    'IT Operations',
    'Network Operations',
    'Compliance',
    'Management'
  ];

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    roleId: getUserRoleId(user),
    department: user?.department || 'Security Operations',
    status: user?.status === 'Inactive' ? 'Inactive' : 'Active',
    client: parseClient(user?.client)
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(resetMutationState());
    // Fetch clients when modal opens
    if (clientsStatus === 'idle') {
      dispatch(fetchClients());
    }
    // Fetch available roles from API
    if (availableRoles.length === 0) {
      dispatch(fetchAvailableRoles());
    }
  }, [dispatch, clientsStatus, availableRoles.length]);

  // Update roleId when roles are loaded and user has only role name
  useEffect(() => {
    if (availableRoles.length > 0 && !formData.roleId && user?.role) {
      const matchingRole = availableRoles.find(r => r.name === user.role);
      if (matchingRole) {
        setFormData(prev => ({ ...prev, roleId: matchingRole.id }));
      }
    }
  }, [availableRoles, formData.roleId, user?.role]);

  useEffect(() => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      roleId: getUserRoleId(user),
      department: user?.department || 'Security Operations',
      status: user?.status === 'Inactive' ? 'Inactive' : 'Active',
      client: parseClient(user?.client)
    });
  }, [user, availableRoles]);

  useEffect(() => {
    return () => {
      dispatch(resetMutationState());
    };
  }, [dispatch]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.roleId) {
      newErrors.roleId = 'Role is required';
    }

    if (!formData.client || !formData.client.id) {
      newErrors.client = 'Client is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Get selected role details
  const selectedRole = useMemo(() => {
    return availableRoles.find(r => r.id === formData.roleId);
  }, [availableRoles, formData.roleId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm() || updateStatus === 'loading') {
      return;
    }

    const payload = {
      ...user,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      roleId: formData.roleId, // UUID of the selected role
      role: selectedRole?.name || '', // Also include role name for display
      department: formData.department,
      status: formData.status,
      client: formData.client,
      lastLogin: user?.lastLogin ?? null,
      createdAt: user?.createdAt ?? new Date().toISOString(),
    };

    try {
      await dispatch(updateUser({ id: user.id, data: payload })).unwrap();
      onClose();
    } catch (submissionError) {
      console.error('Failed to update user:', submissionError);
    }
  };

  const isSubmitting = updateStatus === 'loading' && updatingUserId === user?.id;

  return (
    <div className="modal-overlay">
      <div className="modal modal--large">
        <div className="modal__header">
          <div className="modal__title">
            <User size={20} />
            Edit User
          </div>
          <button className="modal__close" onClick={onClose} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal__content">
          <div className="form-grid">
            <div className="form-section">
              <h4 className="form-section__title">Personal Information</h4>

              <div className="form-row">
                <div className="form-field">
                  <label className="form-field__label">First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`form-field__input ${errors.firstName ? 'form-field__input--error' : ''}`}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && (
                    <span className="form-field__error">{errors.firstName}</span>
                  )}
                </div>

                <div className="form-field">
                  <label className="form-field__label">Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`form-field__input ${errors.lastName ? 'form-field__input--error' : ''}`}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && (
                    <span className="form-field__error">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label className="form-field__label">
                  <Mail size={16} />
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`form-field__input ${errors.email ? 'form-field__input--error' : ''}`}
                  placeholder="user@company.com"
                />
                {errors.email && (
                  <span className="form-field__error">{errors.email}</span>
                )}
              </div>

              <div className="form-field">
                <label className="form-field__label">
                  <Phone size={16} />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`form-field__input ${errors.phone ? 'form-field__input--error' : ''}`}
                  placeholder="+1 (555) 123-4567"
                />
                {errors.phone && (
                  <span className="form-field__error">{errors.phone}</span>
                )}
              </div>
            </div>

            <div className="form-section">
              <h4 className="form-section__title">Role & Department</h4>

              <div className="form-field">
                <label className="form-field__label">
                  <Shield size={16} />
                  Role *
                </label>
                <select
                  value={formData.roleId}
                  onChange={(e) => handleInputChange('roleId', e.target.value)}
                  className={`form-field__select ${errors.roleId ? 'form-field__input--error' : ''}`}
                  disabled={isLoadingRoles}
                >
                  {isLoadingRoles ? (
                    <option value="">Loading roles...</option>
                  ) : (
                    <>
                      <option value="">Select a role</option>
                      {availableRoles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                {errors.roleId && (
                  <span className="form-field__error">{errors.roleId}</span>
                )}
                {selectedRole?.description && (
                  <span className="form-field__hint">{selectedRole.description}</span>
                )}
              </div>

              <div className="form-field">
                <label className="form-field__label">
                  <Building size={16} />
                  Department
                </label>
                <select
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  className="form-field__select"
                >
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label className="form-field__label">
                  <Briefcase size={16} />
                  Client *
                </label>
                <select
                  value={formData.client?.id || ''}
                  onChange={(e) => {
                    const selectedClient = clients.find(c => c.id === e.target.value);
                    handleInputChange('client', selectedClient || null);
                  }}
                  className={`form-field__select ${errors.client ? 'form-field__input--error' : ''}`}
                  disabled={clientsStatus === 'loading'}
                >
                  <option value="">
                    {clientsStatus === 'loading' ? 'Loading clients...' : 'Select a client'}
                  </option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
                {errors.client && (
                  <span className="form-field__error">{errors.client}</span>
                )}
              </div>

              <div className="form-field">
                <label className="form-field__label">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="form-field__select"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Role Info Preview */}
          {selectedRole && (
            <div className="form-section">
              <h4 className="form-section__title">Selected Role</h4>
              <div className="role-preview">
                <div className="role-preview__header">
                  <Shield size={18} />
                  <span className="role-preview__name">{selectedRole.name}</span>
                  {!selectedRole.isEditable && (
                    <span className="chip chip--info">Full Access</span>
                  )}
                </div>
                {selectedRole.description && (
                  <p className="role-preview__description">{selectedRole.description}</p>
                )}
              </div>
            </div>
          )}
        </form>

        <div className="modal__footer">
          <button
            type="button"
            className="btn btn--outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoadingRoles}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Changes
              </>
            )}
          </button>
        </div>

        {updateStatus === 'failed' && updateError && (
          <div className="form-error-alert">
            {updateError}
          </div>
        )}
      </div>
    </div>
  );
}
